import SubstanceBarHtml from "./SubstanceBarHtml.js";
import SubstanceBarItem from "../../js/components/substance-bar/SubstanceBarItem.js";
import StructureCmp from "../../js/components/structure/StructureCmp.js";

export default class SubstanceBarCmp {
    /**
     * If user has ho permission to edit injection or batch and there are no substances, SubstanceBar is not shown
     * When we change view mode in injection page we change SubstanceBar also, but it can be not rendered
     *
     * @type {boolean}
     */
    #isRendered = false;
    /**
     * @type {SubstanceBarHtml}
     */
    #html = new SubstanceBarHtml();
    /**
     * @type {StructureSvgLoader}
     */
    #structureSvgLoader;
    /**
     * SubstanceJson in injection page and string in batch page
     * @type {SubstanceJson[]|string[]}
     */
    #substances = [];
    #callbacks = {
        /**
         * @private
         * @callback SubstanceBarCmp.onEditIconClickCallbackType
         * @param {string} substanceId
         * @param {string} name
         * @param {string} color
         */
        onEditIconClick: (substanceId, name, color) => {/* to be overridden */},
        onSubstanceSelected: [],
        onSubstanceDeselected: [],
        onAddSubstanceClick: [],
        onSizeChanged: []
    }

    constructor(structureSvgLoader) {
        this.#structureSvgLoader = structureSvgLoader;
    }

    /**
     * @param {HTMLElement} parentElement
     * @param  {{highlightAddButtonWhenEmpty?: boolean, showAddButton?:boolean}} options
     */
    init(parentElement, options) {
        this.#html.init(parentElement, options);
    }

    show() {
        this.#isRendered = true;
        this.#html.show();
        this.#html.onAddSubstanceClick(()=>{
            this.#callbacks.onAddSubstanceClick.forEach(cb => cb());
        });
        this.#html.onSubstanceSelected((substanceId, name, color)=>{
            this.#callbacks.onSubstanceSelected.forEach(cb => cb(substanceId, name, color));
        })
        this.#html.onSubstanceDeselected((substanceId)=>{
            this.#callbacks.onSubstanceDeselected.forEach(cb => cb(substanceId));
        });
        this.#html.onSizeChanged((isMinimized)=>{
            this.#callbacks.onSizeChanged.forEach(cb => cb(isMinimized));
        });
    }

    hide() {
        this.#html.hide();
        this.#isRendered = false;
    }

    /** @return {boolean} */
    isRendered(){
        return this.#isRendered
    }

    /**
     * @param {SubstanceJson} substance
     */
    async addSubstance(substance) {
        this.#substances.push(substance);
        this.#substances.sort((a, b)=> a.getDisplayName().localeCompare(b.getDisplayName()));
        const index = this.#substances.indexOf(substance);
        const substanceBarItem = new SubstanceBarItem({
            name: substance.getDisplayName(),
            color: substance.getDisplayColor(),
            substanceId: substance.getId()
        });
        const structureSvg = await this.#structureSvgLoader.getByUrlPath(substance.getStructureSvgPath())
        const struct = new StructureCmp(structureSvg, substance.getMolecularFormula(), substance.getMonoIsotopicMass());
        struct.setAttribute("slot", "structure");
        substanceBarItem.appendChild(struct);
        substanceBarItem.onEditIconClick(({substanceId, name, color})=>{
            this.#callbacks.onEditIconClick(substanceId, name, color);
        });
        this.#html.addSubstanceItem(substanceBarItem, index);
    }

    addSubstanceName(substanceName, color) {
        const substanceBarItem = new SubstanceBarItem({
            name: substanceName,
            color: color,
        });
        substanceBarItem.onEditIconClick(({substanceId, name, color})=>{
            this.#callbacks.onEditIconClick(substanceId, name, color);
        });
        this.#substances.push(substanceName);
        this.#substances.sort((a, b)=> a.localeCompare(b));
        const index = this.#substances.findIndex(s=>s === substanceName);
        this.#html.addSubstanceItem(substanceBarItem, index);
    }

    /**
     * @param {SubstanceJson} substance
     * @returns {Promise<void>}
     */
    async updateSubstance(substance) {
        const existSubstance = this.#substances.find(s => s.getId() === substance.getId());
        this.#substances[this.#substances.indexOf(existSubstance)] = substance;
        this.#html.updateSubstanceItem(substance.getId(), substance.getDisplayName(), substance.getDisplayColor());
    }

    /**
     * @param {string} substanceId
     */
    deleteSubstance(substanceId) {
        this.#html.deleteSubstanceItem(substanceId);
        this.#substances = this.#substances.filter(s => s.getId() !== substanceId);
    }

    removeAllSubstances() {
        this.#html.removeAllSubstances();
    }
    displayAllSubstancesNormal() {
        this.#html.displayAllSubstancesNormal();
    }

    /**
     * @param {boolean} visible
     */
    setVisible(visible) {
        this.#html.setVisible(visible);
    }

    /** @param {boolean} isCompareMode */
    enterCompareMode(isCompareMode){
        this.#html.enterCompareMode(isCompareMode)
    }

    /**
     * @param {SubstanceBar.onSubstanceSelectedCallbackType} cb
     */
    onSubstanceSelected(cb) {
        this.#callbacks.onSubstanceSelected.push(cb);
    }

    selectFirstSubstance(){
        this.#html.selectFirstItem()
    }

    deselectFirstSubstance(){
        this.#html.deselectFirstItem()
    }

    /**
     * @param {SubstanceBar.onSubstanceDeselectedCallbackType} cb
     */
    onSubstanceDeselected(cb) {
        this.#callbacks.onSubstanceDeselected.push(cb);
    }

    /**
     * @param {SubstanceBar.onSizeChangedCallbackType} cb
     */
    onSizeChanged(cb) {
        this.#callbacks.onSizeChanged.push(cb);
    }

    /**
     * @param {SubstanceBarHtml.onAddSubstanceClickCallbackType} cb
     */
    onAddSubstanceClick(cb) {
        this.#callbacks.onAddSubstanceClick.push(cb);
    }

    /**
     * @param {SubstanceBarCmp.onEditIconClickCallbackType} cb
     */
    onEditSubstanceClick(cb) {
        this.#callbacks.onEditIconClick = cb;
    }
}